import { ErrorCode, type ApiErrorBody } from '@brimo/shared';
import { ApiError, GENERIC_ERROR_MESSAGE, UNAUTHENTICATED_EVENT } from '../../lib/api';

/**
 * Multipart upload with progress events (`XMLHttpRequest` reports what `fetch` cannot): the wizard's
 * file rows show "64 %" while a file travels (Objekt anlegen 02), the developer page a bar per request.
 * Errors arrive as the usual JSON envelope and are thrown as `ApiError`; a 401 outside the auth routes
 * ends the session like `lib/api.ts` does.
 */
export interface UploadOptions {
  onProgress?: ((fraction: number) => void) | undefined;
  signal?: AbortSignal | undefined;
}

export function uploadWithProgress<T>(path: string, form: FormData, options: UploadOptions = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `/api${path}`);
    request.setRequestHeader('Accept', 'application/json');
    request.withCredentials = true;
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) options.onProgress?.(event.loaded / event.total);
    };
    request.onerror = () =>
      reject(new ApiError(0, { code: ErrorCode.INTERNAL, message: GENERIC_ERROR_MESSAGE }));
    request.onabort = () => reject(new DOMException('Upload abgebrochen', 'AbortError'));
    request.onload = () => {
      let json: unknown = null;
      try {
        json = request.responseText ? JSON.parse(request.responseText) : null;
      } catch {
        json = null;
      }
      if (request.status >= 200 && request.status < 300) {
        options.onProgress?.(1);
        resolve(json as T);
        return;
      }
      const body = (json as Partial<ApiErrorBody> | null)?.error ?? {
        code: ErrorCode.INTERNAL,
        message: request.status === 413 ? 'Die Datei ist zu groß.' : GENERIC_ERROR_MESSAGE,
      };
      if (request.status === 401 && body.code === ErrorCode.UNAUTHENTICATED && !path.startsWith('/auth/')) {
        window.dispatchEvent(new Event(UNAUTHENTICATED_EVENT));
      }
      reject(new ApiError(request.status, body));
    };
    if (options.signal) {
      if (options.signal.aborted) {
        reject(new DOMException('Upload abgebrochen', 'AbortError'));
        return;
      }
      options.signal.addEventListener('abort', () => request.abort(), { once: true });
    }
    request.send(form);
  });
}
